import React, { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Section, Eyebrow, Heading } from "../components/site/Section";

// Simple manifest — add new filenames here as artwork drops in.
// Files live at /app/frontend/public/art/gallery/<name>.
const GALLERY_IMAGES = [
  "s1_01.png",
  "s1_02.png",
  "s1_03.png",
  "s1_05.png",
  "s1_06.png",
  "s1_07.png",
  "s1_08.png",
  "s1_09.png",
  "s1_10.png",
  "s1_11.png",
  "s1_12.png",
  "s1_13.png",
  "s1_14.png",
  "s1_15.png",
];

export default function GalleryPage() {
  const [openIndex, setOpenIndex] = useState(null); // null = closed
  const total = GALLERY_IMAGES.length;

  const close = useCallback(() => setOpenIndex(null), []);
  const prev = useCallback(() => setOpenIndex((i) => (i === null ? null : (i - 1 + total) % total)), [total]);
  const next = useCallback(() => setOpenIndex((i) => (i === null ? null : (i + 1) % total)), [total]);

  // Keyboard nav for the lightbox
  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while lightbox is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [openIndex, close, prev, next]);

  return (
    <div data-testid="gallery-page">
      <Section testId="gallery-hero" className="pt-14 md:pt-24 pb-6">
        <Eyebrow>Gallery</Eyebrow>
        <Heading>A glimpse of the world.</Heading>
        <p className="mt-4 text-sm md:text-base text-[#A39E99] max-w-2xl leading-relaxed">
          Fragments recovered from the Archive — no names, no captions. Look
          closely. The chains run deeper than they seem.
        </p>
      </Section>

      <Section className="pt-0 pb-24">
        {/* CSS columns = masonry-style layout, no JS layout math required. */}
        <div
          data-testid="gallery-grid"
          className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-5 [column-fill:_balance]"
        >
          {GALLERY_IMAGES.map((name, i) => (
            <button
              type="button"
              key={name}
              onClick={() => setOpenIndex(i)}
              data-testid={`gallery-tile-${i}`}
              aria-label={`Open image ${i + 1} of ${total}`}
              className="group relative block w-full mb-4 md:mb-5 overflow-hidden border border-white/5 hover:border-[#C5A059]/40 transition-colors focus:outline-none focus:border-[#C5A059] break-inside-avoid"
            >
              <img
                src={`/art/gallery/${name}`}
                alt=""
                loading="lazy"
                className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]"
                draggable={false}
              />
              {/* Subtle hover overlay */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,0.4) 100%)",
                }}
              />
            </button>
          ))}
        </div>
      </Section>

      {openIndex !== null && (
        <Lightbox
          images={GALLERY_IMAGES}
          index={openIndex}
          onClose={close}
          onPrev={prev}
          onNext={next}
        />
      )}
    </div>
  );
}

function Lightbox({ images, index, onClose, onPrev, onNext }) {
  const src = `/art/gallery/${images[index]}`;
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      data-testid="gallery-lightbox"
      onClick={onClose}
    >
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close viewer"
        data-testid="gallery-lightbox-close"
        className="absolute top-3 right-3 sm:top-6 sm:right-6 w-11 h-11 flex items-center justify-center bg-black/70 border border-white/20 hover:border-[#C5A059] hover:bg-black transition-colors z-10"
      >
        <X size={18} className="text-white" />
      </button>

      {/* Prev / Next */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            aria-label="Previous image"
            data-testid="gallery-lightbox-prev"
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-black/70 border border-white/20 hover:border-[#C5A059] hover:bg-black transition-colors z-10"
          >
            <ChevronLeft size={20} className="text-white" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            aria-label="Next image"
            data-testid="gallery-lightbox-next"
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-black/70 border border-white/20 hover:border-[#C5A059] hover:bg-black transition-colors z-10"
          >
            <ChevronRight size={20} className="text-white" />
          </button>
        </>
      )}

      {/* Image */}
      <img
        src={src}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="max-h-[92vh] max-w-[95vw] w-auto h-auto object-contain select-none"
        draggable={false}
      />

      {/* Index chip */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.32em] uppercase text-[#A39E99] bg-black/60 px-3 py-1.5 border border-white/10">
        {index + 1} / {images.length}
      </div>
    </div>
  );
}
